import React, { useState } from "react";
import {
  generateRhymingDictionary,
  analyzeMeter,
  performWritingQualityAnalysis,
} from "../../utils/textAnalysis";
import { analyzeFullTextRhymes } from "../../utils/phoneticUtils";
import { songVocabularyPhoneticMap } from "../../data/songVocabularyPhoneticMap";
import geminiService from "../../services/geminiService";
import RhymeEditor from "../Analysis/RhymeEditor";
import EditableHighlightedLyrics from "../Analysis/EditableHighlightedLyrics";

const AnalysisTab = ({
  songs,
  selectedSongForAnalysis,
  setSelectedSongForAnalysis,
  analysisResults,
  setAnalysisResults,
  analysisType,
  setAnalysisType,
  onSearchInLyrics,
  stats,
}) => {
  // State for edited lyrics
  const [editedStructuredLyrics, setEditedStructuredLyrics] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Add coherence analysis state
  const [coherenceResults, setCoherenceResults] = useState(null);
  const [coherenceLoading, setCoherenceLoading] = useState(false);
  const [coherenceProgress, setCoherenceProgress] = useState(null);

  // Add performance analysis state
  const [performanceResults, setPerformanceResults] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);

  const handleRhymingDictionary = () => {
    if (songs.length === 0) {
      alert("Please upload some songs first!");
      return;
    }
    const rhymeDict = generateRhymingDictionary(songs);
    setAnalysisResults(rhymeDict);
    setAnalysisType("rhyming-dictionary");
  };

  const handleWritingQuality = () => {
    if (!selectedSongForAnalysis) return;
    const song = songs.find(
      (s) => s.id.toString() === selectedSongForAnalysis.toString(),
    );
    if (!song) return;

    // Clear performance results when running writing quality
    setPerformanceResults(null);

    setAnalysisResults(null);
    setAnalysisType("writing-quality-loading");

    try {
      const qualityAnalysis = performWritingQualityAnalysis(song.lyrics);

      setAnalysisResults({
        song,
        ...qualityAnalysis,
      });
      setAnalysisType("writing-quality");
    } catch (error) {
      console.error("Error in writing quality analysis:", error);
      alert("An error occurred during writing quality analysis.");
      setAnalysisType(null);
    }
  };

  const handleCoherenceAnalysis = async (useMultiPass = true) => {
    if (!selectedSongForAnalysis) return;
    const song = songs.find(
      (s) => s.id.toString() === selectedSongForAnalysis.toString(),
    );
    if (!song) return;

    setCoherenceLoading(true);
    setCoherenceResults(null);
    setCoherenceProgress(null);

    try {
      let result;
      if (useMultiPass) {
        // Use multi-pass analysis with progress callback
        // Wrap callback in try-catch to prevent unhandled errors
        result = await geminiService.analyzeLyricalCoherenceMultiPass(
          song.lyrics,
          song.title,
          (progress) => {
            try {
              setCoherenceProgress(progress);
            } catch (callbackError) {
              console.error("Progress callback error:", callbackError);
            }
          },
        );
      } else {
        // Use single-pass analysis
        result = await geminiService.analyzeLyricalCoherence(
          song.lyrics,
          song.title,
        );
      }
      setCoherenceResults(result);
    } catch (error) {
      console.error("Error in coherence analysis:", error);
      setCoherenceResults({
        success: false,
        error: error.message || "An unexpected error occurred during analysis",
      });
    }

    setCoherenceProgress(null);
    setCoherenceLoading(false);
  };

  const handlePerformanceAnalysis = async (forceFresh = false) => {
    if (!selectedSongForAnalysis) return;
    const song = songs.find(
      (s) => s.id.toString() === selectedSongForAnalysis.toString(),
    );
    if (!song) return;

    console.log(`Performance analysis started. Force fresh: ${forceFresh}`);

    // Clear writing quality results when running performance analysis
    setAnalysisResults(null);
    setAnalysisType(null);

    setPerformanceLoading(true);
    setPerformanceResults(null);

    try {
      const result = await geminiService.analyzePerformanceAndStyle(
        song.lyrics,
        song.title,
        forceFresh,
      );
      setPerformanceResults(result);
      console.log(
        "Performance analysis completed:",
        result.success ? "Success" : "Failed",
      );
    } catch (error) {
      console.error("Error in performance analysis:", error);
      setPerformanceResults({
        success: false,
        error: error.message,
        retryable: true,
      });
    }

    setPerformanceLoading(false);
  };

  const handleRhymeScheme = () => {
    if (!selectedSongForAnalysis) return;
    const song = songs.find(
      (s) => s.id.toString() === selectedSongForAnalysis.toString(),
    );
    if (!song) return;

    setAnalysisResults(null);
    setAnalysisType("rhyme-scheme-loading");

    try {
      const structuredLyrics = analyzeFullTextRhymes(
        song.lyrics,
        songVocabularyPhoneticMap,
      );
      setAnalysisResults({ song, structuredLyrics });
      setAnalysisType("rhyme-scheme");
    } catch (error) {
      console.error("Error in phonetic rhyme analysis:", error);
      alert("An error occurred during rhyme analysis.");
      setAnalysisType(null);
    }
  };

  const handleMeterAnalysis = () => {
    if (!selectedSongForAnalysis) return;
    const song = songs.find(
      (s) => s.id.toString() === selectedSongForAnalysis.toString(),
    );
    if (!song) {
      console.error("Song not found:", selectedSongForAnalysis);
      return;
    }
    try {
      const meterAnalysis = analyzeMeter(song.lyrics);
      setAnalysisResults({ song, meterAnalysis });
      setAnalysisType("meter-analysis");
    } catch (error) {
      console.error("Error in meter analysis:", error);
      alert("Error analyzing syllables. Please try again.");
    }
  };

  return (
    <div>
      {/* Song Selection - Moved to Top */}
      <div className="p-4 rounded-lg border mb-6 bg-forest-800 border-forest-600">
        <h3 className="font-medium mb-2">Select Song to Analyze</h3>
        <select
          value={selectedSongForAnalysis || ""}
          onChange={(e) => setSelectedSongForAnalysis(e.target.value)}
          className="w-full max-w-md p-3 rounded border bg-forest-700 border-forest-600 text-forest-100"
        >
          <option value="">Choose a song...</option>
          {songs.map((song) => (
            <option key={song.id} value={song.id.toString()}>
              {song.title}
            </option>
          ))}
        </select>
      </div>

      {/* Analysis Buttons Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5 mb-6">
        <button
          onClick={handleRhymingDictionary}
          className="p-4 rounded-lg border transition-colors bg-forest-800 border-forest-600 hover:bg-forest-700 text-forest-100"
        >
          <h3 className="font-medium mb-2">Rhyming Dictionary</h3>
          <p className="text-sm text-forest-300">Generate from your lyrics</p>
        </button>

        <button
          onClick={handleRhymeScheme}
          disabled={!selectedSongForAnalysis}
          className={`p-4 rounded-lg border transition-colors ${
            selectedSongForAnalysis
              ? "bg-forest-800 border-forest-600 hover:bg-forest-700 text-forest-100"
              : "bg-forest-700 border-forest-600 text-forest-600 cursor-not-allowed"
          }`}
        >
          <h3 className="font-medium mb-2">Rhyme Scheme</h3>
          <p className="text-sm text-forest-300">Visualize rhyming patterns</p>
        </button>

        <button
          onClick={handleMeterAnalysis}
          disabled={!selectedSongForAnalysis}
          className={`p-4 rounded-lg border transition-colors ${
            selectedSongForAnalysis
              ? "bg-forest-800 border-forest-600 hover:bg-forest-700 text-forest-100"
              : "bg-forest-700 border-forest-600 text-forest-600 cursor-not-allowed"
          }`}
        >
          <h3 className="font-medium mb-2">Syllables</h3>
          <p className="text-sm text-forest-300">
            Count syllables & analyze meter
          </p>
        </button>

        <button
          onClick={() => handlePerformanceAnalysis(true)}
          disabled={performanceLoading}
          className={`p-4 rounded-lg border transition-colors ${
            selectedSongForAnalysis
              ? "bg-forest-800 border-forest-600 hover:bg-forest-700 text-forest-100"
              : "bg-forest-700 border-forest-600 text-forest-600 cursor-not-allowed"
          }`}
        >
          <h3 className="font-medium mb-2">Performance & Style</h3>
          <p className="text-sm text-forest-300">
            AI analysis of vocal flow and style
          </p>
        </button>

        <button
          onClick={handleWritingQuality}
          disabled={!selectedSongForAnalysis}
          className={`p-4 rounded-lg border transition-colors ${
            selectedSongForAnalysis
              ? "bg-forest-800 border-forest-600 hover:bg-forest-700 text-forest-100"
              : "bg-forest-700 border-forest-600 text-forest-600 cursor-not-allowed"
          }`}
        >
          <h3 className="font-medium mb-2">Writing Quality</h3>
          <p className="text-sm text-forest-300">
            Technical and AI Writing Analysis
          </p>
        </button>
      </div>

      {/* Quick Stats - Moved to Bottom */}
      <div className="p-4 rounded-lg border mb-6 bg-forest-800 border-forest-600">
        <h3 className="font-medium mb-2">Quick Stats</h3>
        <div className="text-sm space-y-1 text-forest-300">
          <div>{songs.length} songs loaded</div>
          <div>{stats.totalWords.toLocaleString()} total words</div>
          <div>{stats.uniqueWords.toLocaleString()} unique words</div>
        </div>
      </div>

      {/* Analysis Results */}
      {analysisResults && (
        <div className="rounded-lg border p-6 transition-colors bg-forest-800 border-forest-600">
          {analysisType === "rhyming-dictionary" && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-forest-100 glow-text-medium">
                Your Personal Rhyming Dictionary
              </h3>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {Object.entries(analysisResults)
                  .slice(0, 20)
                  .map(([rhymeKey, words]) => (
                    <div
                      key={rhymeKey}
                      className="p-3 rounded border bg-forest-700 border-forest-600"
                    >
                      <div className="font-medium text-sm mb-2 text-forest-200">
                        Words ending in "-{rhymeKey}"
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {words.map((word, index) => (
                          <span key={word}>
                            <span
                              onClick={() => onSearchInLyrics(word, "search")}
                              className="text-xs px-2 py-1 rounded cursor-pointer transition-colors bg-forest-600 hover:bg-forest-500 text-forest-200"
                            >
                              {word}
                            </span>
                            {index < words.length - 1 && (
                              <span className="text-xs mx-1 text-forest-600">
                                ,&nbsp;&nbsp;
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
              {Object.keys(analysisResults).length > 20 && (
                <p className="mt-4 text-sm text-forest-300">
                  Showing first 20 rhyme groups. Total:{" "}
                  {Object.keys(analysisResults).length}
                </p>
              )}
            </div>
          )}

          {analysisType === "rhyme-scheme-loading" && (
            <div className="text-center py-8 text-forest-300">
              Analyzing Rhymes...
            </div>
          )}

          {analysisType === "rhyme-scheme" && analysisResults && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-forest-100 glow-text-medium">
                Rhyme Analysis: "{analysisResults.song?.title || "Unknown Song"}
                "
              </h3>

              {/* Rhyme Editor */}
              <RhymeEditor
                structuredLyrics={analysisResults.structuredLyrics}
                editedLyrics={
                  editedStructuredLyrics || analysisResults.structuredLyrics
                }
                onLyricsUpdate={setEditedStructuredLyrics}
                songId={analysisResults.song?.id}
                songTitle={analysisResults.song?.title}
                isEditMode={isEditMode}
                setIsEditMode={setIsEditMode}
              />

              {/* Rhyme Visualization */}
              <div className="p-4 rounded-lg border bg-forest-900 border-forest-600">
                <h4 className="text-md font-medium mb-3 text-forest-200">
                  Rhyme Detection
                </h4>
                <EditableHighlightedLyrics
                  structuredLyrics={
                    editedStructuredLyrics || analysisResults.structuredLyrics
                  }
                  isEditMode={isEditMode}
                  onWordClick={(word, lineIndex, wordIndex, event) => {
                    // This will be handled by RhymeEditor through props
                    const rhymeEditor = document.querySelector(
                      "[data-rhyme-editor]",
                    );
                    if (rhymeEditor) {
                      rhymeEditor.dispatchEvent(
                        new CustomEvent("wordClick", {
                          detail: { word, lineIndex, wordIndex, event },
                        }),
                      );
                    }
                  }}
                />
              </div>
            </div>
          )}

          {analysisType === "writing-quality-loading" && (
            <div className="text-center py-8 text-forest-300">
              Analyzing Writing Quality...
            </div>
          )}

          {analysisType === "writing-quality" && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-forest-100 glow-text-medium">
                AI Writing Analysis: "{analysisResults.song.title}"
              </h3>

              {/* AI Coherence Analysis Section */}
              <div className="p-4 rounded border mb-4 bg-forest-700 border-forest-600">
                <div className="flex flex-col gap-3 mb-4">
                  <div className="flex items-center gap-4">
                    <h4 className="font-medium text-forest-100">
                      🤖 AI Writing Analysis
                    </h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleCoherenceAnalysis(true)}
                      disabled={coherenceLoading}
                      className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 border ${
                        coherenceLoading
                          ? "bg-forest-700 text-forest-600 cursor-not-allowed border-forest-600"
                          : "bg-forest-700 hover:bg-forest-600 text-forest-100 border-forest-600"
                      }`}
                    >
                      {coherenceLoading
                        ? "Analyzing..."
                        : "🔬 Deep Analysis (4 Passes)"}
                    </button>
                    <button
                      onClick={() => handleCoherenceAnalysis(false)}
                      disabled={coherenceLoading}
                      className={`px-4 py-2 rounded-lg transition-colors border ${
                        coherenceLoading
                          ? "bg-forest-700 text-forest-600 cursor-not-allowed border-forest-600"
                          : "bg-forest-700 hover:bg-forest-600 text-forest-100 border-forest-600"
                      }`}
                    >
                      ⚡ Quick Analysis
                    </button>
                  </div>
                </div>

                {/* Multi-pass Progress Indicator - Full width below buttons */}
                {coherenceLoading && coherenceProgress && (
                  <div className="w-full p-3 rounded mt-2 bg-forest-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gold-500">
                        {coherenceProgress.label}
                      </span>
                      <span className="text-xs text-forest-300">
                        Pass {coherenceProgress.pass} of{" "}
                        {coherenceProgress.total}
                      </span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden bg-forest-700">
                      <div
                        className={`h-full transition-all duration-500 ${
                          coherenceProgress.status === "waiting"
                            ? "bg-gold-500"
                            : coherenceProgress.status === "analyzing"
                              ? "bg-gold-400 animate-pulse"
                              : "bg-leaf-500"
                        }`}
                        style={{
                          width: `${(coherenceProgress.pass / coherenceProgress.total) * 100}%`,
                        }}
                      />
                    </div>
                    <div className="flex justify-between mt-2">
                      {[1, 2, 3, 4].map((pass) => (
                        <div
                          key={pass}
                          className={`text-xs px-1 py-1 rounded text-center ${
                            coherenceProgress.pass > pass
                              ? "bg-leaf-900 text-leaf-200"
                              : coherenceProgress.pass === pass
                                ? "bg-gold-900 text-gold-200"
                                : "bg-forest-700 text-forest-300"
                          }`}
                        >
                          {pass === 1
                            ? "Narrative"
                            : pass === 2
                              ? "Refs"
                              : pass === 3
                                ? "Craft"
                                : "Final"}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Simple loading indicator for quick analysis */}
                {coherenceLoading && !coherenceProgress && (
                  <div className="text-center py-4 text-forest-300">
                    Analyzing writing quality...
                  </div>
                )}

                {/* Coherence Results */}
                {coherenceResults && (
                  <div>
                    {coherenceResults.success ? (
                      <div className="space-y-4">
                        {/* Cache indicator */}
                        {coherenceResults.fromCache && (
                          <div className="text-xs text-forest-600">
                            📋 Results from cache
                          </div>
                        )}

                        {/* Coherence Score Header */}
                        <div className="p-4 rounded border text-center bg-forest-800 border-forest-600">
                          <div
                            className={`text-4xl font-bold mb-1 ${
                              coherenceResults.coherenceScore >= 80
                                ? "text-leaf-500"
                                : coherenceResults.coherenceScore >= 60
                                  ? "text-gold-500"
                                  : "text-ember-500"
                            }`}
                          >
                            {coherenceResults.coherenceScore}
                          </div>
                          <div className="text-sm text-forest-300">
                            Overall Coherence Score
                          </div>
                        </div>

                        {/* Overall Assessment */}
                        <div className="p-4 rounded border bg-forest-800 border-forest-600">
                          <h5 className="font-medium mb-3 text-leaf-400">
                            📋 Comprehensive Assessment
                          </h5>
                          <p className="text-sm whitespace-pre-line text-forest-200">
                            {coherenceResults.overallAssessment}
                          </p>
                        </div>

                        {/* Narrative Architecture */}
                        {coherenceResults.narrativeArchitecture && (
                          <div className="p-4 rounded border bg-forest-800 border-forest-600">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gold-400">
                                🏛️ Narrative Architecture
                              </h5>
                              <span className="text-xs px-2 py-1 rounded font-medium bg-gold-900 text-gold-200">
                                {coherenceResults.narrativeArchitecture.rating}
                              </span>
                            </div>
                            {coherenceResults.narrativeArchitecture
                              .structure && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Structure:</strong>{" "}
                                {
                                  coherenceResults.narrativeArchitecture
                                    .structure
                                }
                              </p>
                            )}
                            {coherenceResults.narrativeArchitecture
                              .pointOfView && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Point of View:</strong>{" "}
                                {
                                  coherenceResults.narrativeArchitecture
                                    .pointOfView
                                }
                              </p>
                            )}
                            <p className="text-sm text-forest-200">
                              {coherenceResults.narrativeArchitecture.analysis}
                            </p>
                          </div>
                        )}

                        {/* Thematic Analysis */}
                        {coherenceResults.thematicAnalysis && (
                          <div className="p-4 rounded border bg-forest-800 border-forest-600">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-forest-200">
                                🎭 Thematic Analysis
                              </h5>
                              <span className="text-xs px-2 py-1 rounded font-medium bg-forest-700 text-forest-200">
                                {coherenceResults.thematicAnalysis.rating}
                              </span>
                            </div>
                            {coherenceResults.thematicAnalysis.primaryTheme && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Primary Theme:</strong>{" "}
                                {coherenceResults.thematicAnalysis.primaryTheme}
                              </p>
                            )}
                            {coherenceResults.thematicAnalysis.secondaryThemes
                              ?.length > 0 && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Secondary Themes:</strong>{" "}
                                {coherenceResults.thematicAnalysis.secondaryThemes.join(
                                  ", ",
                                )}
                              </p>
                            )}
                            {coherenceResults.thematicAnalysis
                              .thematicEvolution && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Evolution:</strong>{" "}
                                {
                                  coherenceResults.thematicAnalysis
                                    .thematicEvolution
                                }
                              </p>
                            )}
                            <p className="text-sm text-forest-200">
                              {coherenceResults.thematicAnalysis.analysis}
                            </p>
                          </div>
                        )}

                        {/* Imagery & Symbolism */}
                        {coherenceResults.imagerySymbolism && (
                          <div className="p-4 rounded border bg-forest-800 border-forest-600">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-forest-200">
                                🎨 Imagery & Symbolism
                              </h5>
                              <span className="text-xs px-2 py-1 rounded font-medium bg-forest-700 text-forest-200">
                                {coherenceResults.imagerySymbolism.rating}
                              </span>
                            </div>
                            {coherenceResults.imagerySymbolism.dominantImagery
                              ?.length > 0 && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Dominant Imagery:</strong>{" "}
                                {coherenceResults.imagerySymbolism.dominantImagery.join(
                                  ", ",
                                )}
                              </p>
                            )}
                            {coherenceResults.imagerySymbolism.symbols?.length >
                              0 && (
                              <div className="mb-2">
                                <strong className="text-sm text-forest-300">
                                  Symbols:
                                </strong>
                                <ul className="text-sm ml-4 text-forest-300">
                                  {coherenceResults.imagerySymbolism.symbols.map(
                                    (s, i) => (
                                      <li key={i}>
                                        • <strong>{s.symbol}:</strong>{" "}
                                        {s.meaning}
                                      </li>
                                    ),
                                  )}
                                </ul>
                              </div>
                            )}
                            {coherenceResults.imagerySymbolism
                              .metaphorAnalysis && (
                              <div className="mb-3">
                                <strong className="text-sm text-forest-300">
                                  Metaphors:
                                </strong>
                                {Array.isArray(
                                  coherenceResults.imagerySymbolism
                                    .metaphorAnalysis,
                                ) ? (
                                  <div className="space-y-2 mt-1">
                                    {coherenceResults.imagerySymbolism.metaphorAnalysis.map(
                                      (m, i) => (
                                        <div
                                          key={i}
                                          className="text-sm ml-2 p-2 rounded bg-forest-700"
                                        >
                                          <p className="text-forest-200">
                                            "{m.metaphor || m}"
                                          </p>
                                          {m.vehicle && (
                                            <p className="text-xs text-forest-300">
                                              Vehicle: {m.vehicle}
                                            </p>
                                          )}
                                          {m.tenor && (
                                            <p className="text-xs text-forest-300">
                                              Tenor: {m.tenor}
                                            </p>
                                          )}
                                          {m.effectiveness && (
                                            <p className="text-xs text-forest-200">
                                              {m.effectiveness}
                                            </p>
                                          )}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-sm ml-2 mt-1 text-forest-200">
                                    {
                                      coherenceResults.imagerySymbolism
                                        .metaphorAnalysis
                                    }
                                  </p>
                                )}
                              </div>
                            )}
                            <p className="text-sm text-forest-200">
                              {coherenceResults.imagerySymbolism.analysis}
                            </p>
                          </div>
                        )}

                        {/* Emotional Journey */}
                        {coherenceResults.emotionalJourney && (
                          <div className="p-4 rounded border bg-forest-800 border-forest-600">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-ember-400">
                                💖 Emotional Journey
                              </h5>
                              <span className="text-xs px-2 py-1 rounded font-medium bg-ember-900 text-ember-200">
                                {coherenceResults.emotionalJourney.rating}
                              </span>
                            </div>
                            {coherenceResults.emotionalJourney.arc?.length >
                              0 && (
                              <div className="flex items-center gap-2 flex-wrap mb-3">
                                {coherenceResults.emotionalJourney.arc.map(
                                  (emotion, index) => (
                                    <React.Fragment key={index}>
                                      <span className="px-2 py-1 rounded text-xs bg-ember-900 text-ember-200">
                                        {emotion}
                                      </span>
                                      {index <
                                        coherenceResults.emotionalJourney.arc
                                          .length -
                                          1 && (
                                        <span className="text-sm text-forest-300">
                                          →
                                        </span>
                                      )}
                                    </React.Fragment>
                                  ),
                                )}
                              </div>
                            )}
                            {coherenceResults.emotionalJourney.turningPoints
                              ?.length > 0 && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Turning Points:</strong>{" "}
                                {coherenceResults.emotionalJourney.turningPoints.join(
                                  "; ",
                                )}
                              </p>
                            )}
                            {coherenceResults.emotionalJourney.authenticity && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Authenticity:</strong>{" "}
                                {coherenceResults.emotionalJourney.authenticity}
                              </p>
                            )}
                            <p className="text-sm text-forest-200">
                              {coherenceResults.emotionalJourney.analysis}
                            </p>
                          </div>
                        )}

                        {/* Literary Techniques */}
                        {coherenceResults.literaryTechniques && (
                          <div className="p-4 rounded border bg-forest-800 border-forest-600">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gold-400">
                                ✍️ Literary Techniques
                              </h5>
                              <span className="text-xs px-2 py-1 rounded font-medium bg-gold-900 text-gold-200">
                                {coherenceResults.literaryTechniques.rating}
                              </span>
                            </div>
                            {coherenceResults.literaryTechniques.techniques
                              ?.length > 0 && (
                              <div className="mb-3 space-y-2">
                                {coherenceResults.literaryTechniques.techniques.map(
                                  (t, i) => (
                                    <div
                                      key={i}
                                      className="p-2 rounded bg-forest-700"
                                    >
                                      <span className="font-medium text-sm text-gold-300">
                                        {t.technique}:
                                      </span>
                                      <span className="text-sm ml-2 text-forest-200">
                                        {t.usage}
                                      </span>
                                      {t.effect && (
                                        <span className="text-sm ml-1 text-forest-300">
                                          — {t.effect}
                                        </span>
                                      )}
                                    </div>
                                  ),
                                )}
                              </div>
                            )}
                            <p className="text-sm text-forest-200">
                              {coherenceResults.literaryTechniques.analysis}
                            </p>
                          </div>
                        )}

                        {/* Structural Cohesion */}
                        {coherenceResults.structuralCohesion && (
                          <div className="p-4 rounded border bg-forest-800 border-forest-600">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-leaf-400">
                                🔗 Structural Cohesion
                              </h5>
                              <span className="text-xs px-2 py-1 rounded font-medium bg-leaf-900 text-leaf-200">
                                {coherenceResults.structuralCohesion.rating}
                              </span>
                            </div>
                            {coherenceResults.structuralCohesion
                              .sectionFlow && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Section Flow:</strong>{" "}
                                {
                                  coherenceResults.structuralCohesion
                                    .sectionFlow
                                }
                              </p>
                            )}
                            {coherenceResults.structuralCohesion
                              .chorusFunction && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Chorus Function:</strong>{" "}
                                {
                                  coherenceResults.structuralCohesion
                                    .chorusFunction
                                }
                              </p>
                            )}
                            {coherenceResults.structuralCohesion
                              .bridgeFunction && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Bridge Function:</strong>{" "}
                                {
                                  coherenceResults.structuralCohesion
                                    .bridgeFunction
                                }
                              </p>
                            )}
                            <p className="text-sm text-forest-200">
                              {coherenceResults.structuralCohesion.analysis}
                            </p>
                          </div>
                        )}

                        {/* Sound Craft (Multi-pass only) */}
                        {coherenceResults.soundCraft && (
                          <div className="p-4 rounded border bg-forest-800 border-forest-600">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gold-400">
                                🎵 Sound Craft
                              </h5>
                              <span className="text-xs px-2 py-1 rounded font-medium bg-gold-900 text-gold-200">
                                {coherenceResults.soundCraft.rating}
                              </span>
                            </div>
                            {coherenceResults.soundCraft.rhymeScheme && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Rhyme Scheme:</strong>{" "}
                                {coherenceResults.soundCraft.rhymeScheme}
                              </p>
                            )}
                            {coherenceResults.soundCraft.musicality && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Musicality:</strong>{" "}
                                {coherenceResults.soundCraft.musicality}
                              </p>
                            )}
                            {coherenceResults.soundCraft.soundDevices?.length >
                              0 && (
                              <div className="mb-3 space-y-1">
                                <strong className="text-sm text-forest-300">
                                  Sound Devices:
                                </strong>
                                {coherenceResults.soundCraft.soundDevices.map(
                                  (d, i) => (
                                    <div
                                      key={i}
                                      className="text-sm ml-2 text-gold-300"
                                    >
                                      • <strong>{d.device}:</strong> "
                                      {d.example}" — {d.effect}
                                    </div>
                                  ),
                                )}
                              </div>
                            )}
                            {coherenceResults.soundCraft.analysis && (
                              <p className="text-sm text-forest-200">
                                {coherenceResults.soundCraft.analysis}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Intertextuality (Multi-pass only) */}
                        {coherenceResults.intertextuality && (
                          <div className="p-4 rounded border bg-forest-800 border-forest-600">
                            <h5 className="font-medium mb-3 text-forest-200">
                              📖 Intertextuality
                            </h5>
                            {coherenceResults.intertextuality.primaryInfluences
                              ?.length > 0 && (
                              <div className="mb-2">
                                <strong className="text-sm text-forest-300">
                                  Primary Influences:
                                </strong>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {coherenceResults.intertextuality.primaryInfluences.map(
                                    (inf, i) => (
                                      <span
                                        key={i}
                                        className="text-xs px-2 py-1 rounded bg-forest-700 text-forest-200"
                                      >
                                        {inf}
                                      </span>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                            {coherenceResults.intertextuality.dialogueWith && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>In Dialogue With:</strong>{" "}
                                {coherenceResults.intertextuality.dialogueWith}
                              </p>
                            )}
                            {coherenceResults.intertextuality
                              .originalityAssessment && (
                              <p className="text-sm text-forest-200">
                                <strong>Originality:</strong>{" "}
                                {
                                  coherenceResults.intertextuality
                                    .originalityAssessment
                                }
                              </p>
                            )}
                          </div>
                        )}

                        {/* Cultural Context (Multi-pass only) */}
                        {coherenceResults.culturalContext && (
                          <div className="p-4 rounded border bg-forest-800 border-forest-600">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-leaf-400">
                                🌍 Cultural Context
                              </h5>
                              {coherenceResults.culturalContext
                                .accessibilityScore && (
                                <span className="text-xs px-2 py-1 rounded font-medium bg-leaf-900 text-leaf-200">
                                  Accessibility:{" "}
                                  {
                                    coherenceResults.culturalContext
                                      .accessibilityScore
                                  }
                                  /10
                                </span>
                              )}
                            </div>
                            {coherenceResults.culturalContext.eraMarkers
                              ?.length > 0 && (
                              <div className="mb-2">
                                <strong className="text-sm text-forest-300">
                                  Era Markers:
                                </strong>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {coherenceResults.culturalContext.eraMarkers.map(
                                    (marker, i) => (
                                      <span
                                        key={i}
                                        className="text-xs px-2 py-1 rounded bg-leaf-900 text-leaf-200"
                                      >
                                        {marker}
                                      </span>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}
                            {coherenceResults.culturalContext
                              .audienceAssumptions && (
                              <p className="text-sm text-forest-200">
                                <strong>Audience Assumptions:</strong>{" "}
                                {
                                  coherenceResults.culturalContext
                                    .audienceAssumptions
                                }
                              </p>
                            )}
                          </div>
                        )}

                        {/* Craft Verdict (Multi-pass only) */}
                        {coherenceResults.craftVerdict && (
                          <div className="p-4 rounded border bg-forest-800 border-forest-600">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gold-400">
                                ⚖️ Craft Verdict
                              </h5>
                              {coherenceResults.craftVerdict
                                .overallCraftRating && (
                                <span
                                  className={`text-lg font-bold ${
                                    coherenceResults.craftVerdict
                                      .overallCraftRating >= 80
                                      ? "text-leaf-500"
                                      : coherenceResults.craftVerdict
                                            .overallCraftRating >= 60
                                        ? "text-gold-500"
                                        : "text-ember-500"
                                  }`}
                                >
                                  {
                                    coherenceResults.craftVerdict
                                      .overallCraftRating
                                  }
                                  /100
                                </span>
                              )}
                            </div>
                            {coherenceResults.craftVerdict.greatestStrength && (
                              <div className="p-2 rounded mb-2 bg-leaf-900/30">
                                <p className="text-sm text-leaf-200">
                                  <strong>Greatest Strength:</strong>{" "}
                                  {
                                    coherenceResults.craftVerdict
                                      .greatestStrength
                                  }
                                </p>
                              </div>
                            )}
                            {coherenceResults.craftVerdict.biggestWeakness && (
                              <div className="p-2 rounded mb-2 bg-ember-900/30">
                                <p className="text-sm text-ember-200">
                                  <strong>Area for Improvement:</strong>{" "}
                                  {
                                    coherenceResults.craftVerdict
                                      .biggestWeakness
                                  }
                                </p>
                              </div>
                            )}
                            {coherenceResults.craftVerdict.comparableTo && (
                              <p className="text-sm text-forest-200">
                                <strong>Comparable To:</strong>{" "}
                                {coherenceResults.craftVerdict.comparableTo}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Ambiguity & Multivalence (Multi-pass only) */}
                        {coherenceResults.ambiguityMultivalence && (
                          <div className="p-4 rounded border bg-forest-800 border-forest-600">
                            <h5 className="font-medium mb-3 text-forest-200">
                              🔮 Ambiguity & Multiple Meanings
                            </h5>
                            {coherenceResults.ambiguityMultivalence
                              .intentionalAmbiguity?.length > 0 && (
                              <div className="mb-3 space-y-2">
                                {coherenceResults.ambiguityMultivalence.intentionalAmbiguity.map(
                                  (amb, i) => (
                                    <div
                                      key={i}
                                      className="p-2 rounded bg-forest-700"
                                    >
                                      <p className="text-sm font-medium text-forest-200">
                                        "{amb.moment}"
                                      </p>
                                      <ul className="text-sm ml-4 mt-1 text-forest-200">
                                        {amb.interpretations?.map(
                                          (interp, j) => (
                                            <li key={j}>• {interp}</li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  ),
                                )}
                              </div>
                            )}
                            {coherenceResults.ambiguityMultivalence
                              .unsaidImplied && (
                              <p className="text-sm mb-2 text-forest-300">
                                <strong>Unsaid/Implied:</strong>{" "}
                                {
                                  coherenceResults.ambiguityMultivalence
                                    .unsaidImplied
                                }
                              </p>
                            )}
                            {coherenceResults.ambiguityMultivalence
                              .openEndedness && (
                              <p className="text-sm text-forest-200">
                                <strong>Open-Endedness:</strong>{" "}
                                {
                                  coherenceResults.ambiguityMultivalence
                                    .openEndedness
                                }
                              </p>
                            )}
                          </div>
                        )}

                        {/* Multi-pass indicator */}
                        {coherenceResults.multiPass && (
                          <div className="p-2 rounded text-center bg-gold-900/30">
                            <span className="text-xs text-gold-400">
                              🔬 Deep Analysis Complete (4 Passes)
                            </span>
                          </div>
                        )}

                        {/* Legacy ratings display for backward compatibility */}
                        {(coherenceResults.storyFlow ||
                          coherenceResults.thematicUnity) &&
                          !coherenceResults.narrativeArchitecture && (
                            <div className="grid gap-4 md:grid-cols-4 mb-4">
                              {coherenceResults.storyFlow && (
                                <div className="p-3 rounded border text-center bg-forest-800 border-forest-600">
                                  <div className="text-sm font-bold text-forest-100">
                                    {coherenceResults.storyFlow
                                      .charAt(0)
                                      .toUpperCase() +
                                      coherenceResults.storyFlow.slice(1)}
                                  </div>
                                  <div className="text-xs text-forest-300">
                                    Story Flow
                                  </div>
                                </div>
                              )}
                              {coherenceResults.thematicUnity && (
                                <div className="p-3 rounded border text-center bg-forest-800 border-forest-600">
                                  <div className="text-sm font-bold text-forest-100">
                                    {coherenceResults.thematicUnity
                                      .charAt(0)
                                      .toUpperCase() +
                                      coherenceResults.thematicUnity.slice(1)}
                                  </div>
                                  <div className="text-xs text-forest-300">
                                    Theme Unity
                                  </div>
                                </div>
                              )}
                              {coherenceResults.narrativeConsistency && (
                                <div className="p-3 rounded border text-center bg-forest-800 border-forest-600">
                                  <div className="text-sm font-bold text-forest-100">
                                    {coherenceResults.narrativeConsistency
                                      .charAt(0)
                                      .toUpperCase() +
                                      coherenceResults.narrativeConsistency.slice(
                                        1,
                                      )}
                                  </div>
                                  <div className="text-xs text-forest-300">
                                    Consistency
                                  </div>
                                </div>
                              )}
                              {coherenceResults.sectionConnections && (
                                <div className="p-3 rounded border text-center bg-forest-800 border-forest-600">
                                  <div className="text-sm font-bold text-forest-100">
                                    {coherenceResults.sectionConnections
                                      .charAt(0)
                                      .toUpperCase() +
                                      coherenceResults.sectionConnections.slice(
                                        1,
                                      )}
                                  </div>
                                  <div className="text-xs text-forest-300">
                                    Connections
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                        {/* References */}
                        {coherenceResults.references &&
                          coherenceResults.references.length > 0 && (
                            <div className="rounded border mt-4 overflow-hidden bg-forest-800 border-forest-600">
                              <div className="p-3 border-b border-forest-600 bg-forest-700">
                                <h5 className="font-medium text-forest-200">
                                  📚 Cultural References & Allusions
                                </h5>
                              </div>

                              {/* Desktop Table View */}
                              <div className="hidden md:block overflow-x-auto">
                                <table className="w-full">
                                  <thead className="bg-forest-700">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider border-b text-forest-200 border-forest-600">
                                        Type
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider border-b text-forest-200 border-forest-600">
                                        Reference
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider border-b text-forest-200 border-forest-600">
                                        Context
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider border-b text-forest-200 border-forest-600">
                                        Explanation
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {coherenceResults.references.map(
                                      (ref, index) => (
                                        <tr
                                          key={index}
                                          className={`border-b border-forest-600 ${
                                            index % 2 === 0
                                              ? "bg-forest-800"
                                              : "bg-forest-700"
                                          }`}
                                        >
                                          <td className="px-3 py-3 align-top">
                                            <span className="inline-block text-xs px-2 py-1 rounded uppercase font-semibold bg-forest-700 text-forest-200">
                                              {ref.type
                                                .charAt(0)
                                                .toUpperCase() +
                                                ref.type.slice(1)}
                                            </span>
                                          </td>
                                          <td className="px-3 py-3 align-top font-medium text-sm text-forest-100">
                                            {ref.reference}
                                          </td>
                                          <td className="px-3 py-3 align-top text-sm text-forest-200">
                                            {ref.context || "—"}
                                          </td>
                                          <td className="px-3 py-3 align-top text-sm text-forest-200">
                                            {ref.explanation}
                                          </td>
                                        </tr>
                                      ),
                                    )}
                                  </tbody>
                                </table>
                              </div>

                              {/* Mobile Card View */}
                              <div className="md:hidden space-y-3 p-3">
                                {coherenceResults.references.map(
                                  (ref, index) => (
                                    <div
                                      key={index}
                                      className="p-3 rounded border bg-forest-700 border-forest-600"
                                    >
                                      {/* Reference Header */}
                                      <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                          <h6 className="font-medium text-sm text-forest-100">
                                            {ref.reference}
                                          </h6>
                                        </div>
                                        <span className="ml-2 inline-block text-xs px-2 py-1 rounded uppercase font-semibold flex-shrink-0 bg-forest-600 text-forest-200">
                                          {ref.type.charAt(0).toUpperCase() +
                                            ref.type.slice(1)}
                                        </span>
                                      </div>

                                      {/* Context (if available) */}
                                      {ref.context && ref.context !== "—" && (
                                        <div className="mb-2">
                                          <span className="text-xs font-medium text-forest-300">
                                            Context:
                                          </span>
                                          <span className="text-sm ml-1 text-forest-200">
                                            {ref.context}
                                          </span>
                                        </div>
                                      )}

                                      {/* Explanation */}
                                      <div>
                                        <span className="text-xs font-medium text-forest-300">
                                          Explanation:
                                        </span>
                                        <p className="text-sm mt-1 text-forest-200">
                                          {ref.explanation}
                                        </p>
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    ) : (
                      <div
                        className={`p-3 rounded border ${
                          coherenceResults.needsApiKey
                            ? "bg-gold-900/30 border-gold-700"
                            : "bg-ember-900/30 border-ember-700"
                        }`}
                      >
                        {coherenceResults.needsApiKey ? (
                          <div>
                            <p className="text-sm font-medium mb-2 text-gold-300">
                              🔑 API Key Required
                            </p>
                            <p className="text-sm mb-2 text-gold-400">
                              AI analysis requires a Google Gemini API key. Go
                              to <strong>More → AI Settings</strong> to add your
                              free API key.
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-ember-300">
                            ❌ Analysis failed: {coherenceResults.error}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {analysisType === "meter-analysis" && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-forest-100 glow-text-medium">
                Syllable & Meter Analysis: "{analysisResults.song.title}"
              </h3>

              <div className="space-y-2">
                {analysisResults.meterAnalysis.map((lineData, index) => (
                  <div key={index} className="flex items-center gap-4 py-2">
                    <span className="w-12 text-center font-mono text-sm font-bold flex-shrink-0 text-gold-500">
                      {lineData.syllables}
                    </span>
                    <span className="flex-1 text-forest-200">
                      {lineData.line.trim() || "(empty line)"}
                    </span>
                    <span className="text-xs flex-shrink-0 text-forest-600">
                      {lineData.words} words
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-4 rounded border bg-forest-700 border-forest-600">
                <div className="text-sm space-y-1 text-forest-300">
                  <div>
                    <strong>Average syllables per line:</strong>{" "}
                    {(
                      analysisResults.meterAnalysis.reduce(
                        (sum, line) => sum + line.syllables,
                        0,
                      ) /
                      Math.max(
                        1,
                        analysisResults.meterAnalysis.filter((line) =>
                          line.line.trim(),
                        ).length,
                      )
                    ).toFixed(1)}
                  </div>
                  <div>
                    <strong>Total lines:</strong>{" "}
                    {
                      analysisResults.meterAnalysis.filter((line) =>
                        line.line.trim(),
                      ).length
                    }
                  </div>
                  <div>
                    <strong>Syllable range:</strong>{" "}
                    {Math.min(
                      ...analysisResults.meterAnalysis
                        .filter((line) => line.line.trim())
                        .map((line) => line.syllables),
                    )}{" "}
                    -{" "}
                    {Math.max(
                      ...analysisResults.meterAnalysis
                        .filter((line) => line.line.trim())
                        .map((line) => line.syllables),
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Performance Analysis Loading */}
      {performanceLoading && (
        <div className="p-4 rounded border mt-6 bg-forest-800 border-forest-600">
          <div className="text-center py-8 text-forest-300">
            🎤 Analyzing Performance & Style...
          </div>
        </div>
      )}

      {/* Performance Analysis Results - COMPLETELY SEPARATE SECTION */}
      {performanceResults && (
        <div className="rounded border mt-6 bg-forest-800 border-forest-600">
          <div className="p-4 border-b border-forest-600 bg-forest-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-forest-100">
                🎤 Performance & Style Analysis: "
                {
                  songs.find((s) => s.id.toString() === selectedSongForAnalysis)
                    ?.title
                }
                "
              </h3>
              <button
                onClick={() => handlePerformanceAnalysis(false)}
                disabled={performanceLoading}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  performanceLoading
                    ? "bg-forest-700 text-forest-600 cursor-not-allowed"
                    : "bg-forest-700 text-forest-200 hover:bg-forest-600"
                }`}
              >
                {performanceLoading ? "Analyzing..." : "Re-analyze"}
              </button>
            </div>
            {performanceResults.fromCache && (
              <div className="text-xs mt-2 text-forest-600">
                📋 Results from cache
              </div>
            )}
          </div>

          <div className="p-4">
            {performanceResults.success ? (
              <div className="space-y-6">
                {/* Vocal Flow Section */}
                <div className="p-4 rounded border bg-forest-700 border-forest-600">
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-gold-400">
                    🌊 Vocal Flow
                    <span
                      className={`text-sm px-2 py-1 rounded ${
                        performanceResults.vocalFlow.overallRating === "smooth"
                          ? "bg-leaf-800 text-leaf-200"
                          : performanceResults.vocalFlow.overallRating ===
                              "complex"
                            ? "bg-gold-800 text-gold-200"
                            : performanceResults.vocalFlow.overallRating ===
                                "choppy"
                              ? "bg-ember-800 text-ember-200"
                              : performanceResults.vocalFlow.overallRating ===
                                  "varied"
                                ? "bg-forest-600 text-forest-200"
                                : "bg-forest-700 text-forest-200"
                      }`}
                    >
                      {performanceResults.vocalFlow.overallRating}
                    </span>
                  </h4>
                  {performanceResults.vocalFlow.flowPatterns.length > 0 && (
                    <div className="mb-3">
                      <h5 className="text-sm font-medium mb-2 text-forest-200">
                        Flow Patterns:
                      </h5>
                      <ul className="text-sm space-y-1 text-forest-200">
                        {performanceResults.vocalFlow.flowPatterns.map(
                          (pattern, index) => (
                            <li key={index}>• {pattern}</li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}
                  {performanceResults.vocalFlow.difficultSections &&
                    performanceResults.vocalFlow.difficultSections.length >
                      0 && (
                      <div>
                        <h5 className="text-sm font-medium mb-2 text-gold-400">
                          Challenging Sections:
                        </h5>
                        <ul className="text-sm space-y-1 text-gold-300">
                          {performanceResults.vocalFlow.difficultSections.map(
                            (section, index) => (
                              <li key={index}>⚠️ {section}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                </div>

                {/* Breath Control Section */}
                <div className="p-4 rounded border bg-forest-700 border-forest-600">
                  <h4 className="font-medium mb-3 flex items-center gap-2 text-leaf-400">
                    💨 Breath Control
                    <span
                      className={`text-sm px-2 py-1 rounded ${
                        performanceResults.breathControl.rating === "excellent"
                          ? "bg-leaf-800 text-leaf-200"
                          : performanceResults.breathControl.rating === "good"
                            ? "bg-forest-600 text-forest-200"
                            : performanceResults.breathControl.rating === "fair"
                              ? "bg-gold-800 text-gold-200"
                              : "bg-ember-800 text-ember-200"
                      }`}
                    >
                      {performanceResults.breathControl.rating}
                    </span>
                  </h4>
                  <div className="grid gap-3 md:grid-cols-2">
                    {performanceResults.breathControl.naturalBreaks &&
                      performanceResults.breathControl.naturalBreaks.length >
                        0 && (
                        <div>
                          <h5 className="text-sm font-medium mb-2 text-leaf-400">
                            Natural Breaks:
                          </h5>
                          <ul className="text-sm space-y-1 text-leaf-300">
                            {performanceResults.breathControl.naturalBreaks.map(
                              (breakPoint, index) => (
                                <li key={index}>✅ {breakPoint}</li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                    {performanceResults.breathControl.challengingSections &&
                      performanceResults.breathControl.challengingSections
                        .length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium mb-2 text-ember-400">
                            Challenging Sections:
                          </h5>
                          <ul className="text-sm space-y-1 text-ember-300">
                            {performanceResults.breathControl.challengingSections.map(
                              (section, index) => (
                                <li key={index}>⚠️ {section}</li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                  </div>
                </div>

                {/* Energy & Mood Dynamics Section */}
                {performanceResults.performanceDynamics.energyMapping &&
                  performanceResults.performanceDynamics.energyMapping.length >
                    0 && (
                    <div className="p-4 rounded border bg-forest-700 border-forest-600">
                      <h4 className="font-medium mb-3 text-forest-200">
                        ⚡ Energy & Mood Dynamics
                      </h4>
                      <div className="space-y-2">
                        {performanceResults.performanceDynamics.energyMapping.map(
                          (mapping, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-3"
                            >
                              <span className="text-sm font-medium min-w-20 text-forest-200">
                                {mapping.section}:
                              </span>
                              <span
                                className={`text-xs px-2 py-1 rounded font-medium ${
                                  mapping.energy === "high"
                                    ? "bg-ember-800 text-ember-200"
                                    : mapping.energy === "medium"
                                      ? "bg-gold-800 text-gold-200"
                                      : "bg-forest-600 text-forest-200"
                                }`}
                              >
                                {mapping.energy}
                              </span>
                              <span className="text-sm text-forest-200">
                                {mapping.description}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {/* Repetition Analysis Section */}
                <div className="p-4 rounded border bg-forest-700 border-forest-600">
                  <h4 className="font-medium mb-3 text-gold-400">
                    🔄 Repetition Analysis
                  </h4>
                  <div className="grid gap-3 md:grid-cols-3">
                    {performanceResults.repetitionAnalysis.effectiveRepeats &&
                      performanceResults.repetitionAnalysis.effectiveRepeats
                        .length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium mb-2 text-leaf-400">
                            Effective Repeats:
                          </h5>
                          <ul className="text-sm space-y-1 text-leaf-300">
                            {performanceResults.repetitionAnalysis.effectiveRepeats.map(
                              (repeat, index) => (
                                <li key={index}>✅ {repeat}</li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                    {performanceResults.repetitionAnalysis.overusedPhrases &&
                      performanceResults.repetitionAnalysis.overusedPhrases
                        .length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium mb-2 text-ember-400">
                            Overused:
                          </h5>
                          <ul className="text-sm space-y-1 text-ember-300">
                            {performanceResults.repetitionAnalysis.overusedPhrases.map(
                              (phrase, index) => (
                                <li key={index}>⚠️ {phrase}</li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                    {performanceResults.repetitionAnalysis
                      .missedOpportunities &&
                      performanceResults.repetitionAnalysis.missedOpportunities
                        .length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium mb-2 text-gold-400">
                            Opportunities:
                          </h5>
                          <ul className="text-sm space-y-1 text-gold-300">
                            {performanceResults.repetitionAnalysis.missedOpportunities.map(
                              (opportunity, index) => (
                                <li key={index}>💡 {opportunity}</li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                  </div>
                </div>

                {/* Emotional Progression Section */}
                <div className="p-4 rounded border bg-forest-700 border-forest-600">
                  <h4 className="font-medium mb-3 text-forest-200">
                    💭 Emotional Progression
                  </h4>
                  {performanceResults.emotionalProgression.arc &&
                    performanceResults.emotionalProgression.arc.length > 0 && (
                      <div className="mb-3">
                        <h5 className="text-sm font-medium mb-2 text-forest-200">
                          Emotional Arc:
                        </h5>
                        <div className="flex items-center gap-2 flex-wrap">
                          {performanceResults.emotionalProgression.arc.map(
                            (emotion, index) => (
                              <React.Fragment key={index}>
                                <span className="px-2 py-1 rounded text-sm bg-ember-800 text-ember-200">
                                  {emotion}
                                </span>
                                {index <
                                  performanceResults.emotionalProgression.arc
                                    .length -
                                    1 && (
                                  <span className="text-sm text-forest-300">
                                    →
                                  </span>
                                )}
                              </React.Fragment>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                  {performanceResults.emotionalProgression.keyMoments &&
                    performanceResults.emotionalProgression.keyMoments.length >
                      0 && (
                      <div>
                        <h5 className="text-sm font-medium mb-2 text-forest-200">
                          Key Emotional Moments:
                        </h5>
                        <ul className="text-sm space-y-1 text-ember-300">
                          {performanceResults.emotionalProgression.keyMoments.map(
                            (moment, index) => (
                              <li key={index}>🎯 {moment}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                </div>

                {/* Era/Influence Section */}
                <div className="p-4 rounded border bg-forest-700 border-forest-600">
                  <h4 className="font-medium mb-3 text-forest-200">
                    🎵 Era & Influence Detection
                  </h4>
                  <div className="grid gap-3 md:grid-cols-3">
                    <div>
                      <h5 className="text-sm font-medium mb-2 text-forest-200">
                        Primary Era:
                      </h5>
                      <span className="inline-block px-3 py-1 rounded text-sm font-medium bg-forest-600 text-forest-200">
                        {performanceResults.eraInfluence.primaryEra}
                      </span>
                    </div>
                    {performanceResults.eraInfluence.influences &&
                      performanceResults.eraInfluence.influences.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium mb-2 text-forest-200">
                            Influences:
                          </h5>
                          <ul className="text-sm space-y-1 text-forest-200">
                            {performanceResults.eraInfluence.influences.map(
                              (influence, index) => (
                                <li key={index}>🎸 {influence}</li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                    {performanceResults.eraInfluence.modernElements &&
                      performanceResults.eraInfluence.modernElements.length >
                        0 && (
                        <div>
                          <h5 className="text-sm font-medium mb-2 text-forest-200">
                            Modern Elements:
                          </h5>
                          <ul className="text-sm space-y-1 text-forest-200">
                            {performanceResults.eraInfluence.modernElements.map(
                              (element, index) => (
                                <li key={index}>✨ {element}</li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                  </div>
                </div>

                {/* Hook Analysis Section (NEW) */}
                {performanceResults.hookAnalysis && (
                  <div className="p-4 rounded border bg-forest-700 border-forest-600">
                    <h4 className="font-medium mb-3 text-gold-400">
                      🎣 Hook Analysis
                    </h4>
                    {performanceResults.hookAnalysis.primaryHook && (
                      <div className="p-3 rounded mb-3 bg-gold-900/30">
                        <p className="text-sm font-medium text-gold-300">
                          Primary Hook: "
                          {performanceResults.hookAnalysis.primaryHook}"
                        </p>
                      </div>
                    )}
                    {performanceResults.hookAnalysis.secondaryHooks?.length >
                      0 && (
                      <div className="mb-3">
                        <h5 className="text-sm font-medium mb-2 text-forest-200">
                          Secondary Hooks:
                        </h5>
                        <ul className="text-sm space-y-1 text-gold-300">
                          {performanceResults.hookAnalysis.secondaryHooks.map(
                            (hook, index) => (
                              <li key={index}>🎵 {hook}</li>
                            ),
                          )}
                        </ul>
                      </div>
                    )}
                    <div className="grid gap-3 md:grid-cols-2">
                      {performanceResults.hookAnalysis.earwormRating && (
                        <div className="p-2 rounded text-center bg-forest-800">
                          <div className="text-xl font-bold text-gold-400">
                            {performanceResults.hookAnalysis.earwormRating}/10
                          </div>
                          <div className="text-xs text-forest-300">
                            Earworm Rating
                          </div>
                        </div>
                      )}
                      {performanceResults.hookAnalysis.singalongPotential && (
                        <div className="p-2 rounded text-center bg-forest-800">
                          <div className="text-xl font-bold text-gold-400">
                            {performanceResults.hookAnalysis.singalongPotential}
                            /10
                          </div>
                          <div className="text-xs text-forest-300">
                            Singalong Potential
                          </div>
                        </div>
                      )}
                    </div>
                    {performanceResults.hookAnalysis.hookExplanation && (
                      <p className="text-sm mt-3 text-forest-200">
                        {performanceResults.hookAnalysis.hookExplanation}
                      </p>
                    )}
                  </div>
                )}

                {/* Rhythmic Analysis Section (NEW) */}
                {performanceResults.rhythmicAnalysis && (
                  <div className="p-4 rounded border bg-forest-700 border-forest-600">
                    <h4 className="font-medium mb-3 text-leaf-400">
                      🥁 Rhythmic Analysis
                    </h4>
                    <div className="grid gap-3 md:grid-cols-2">
                      {performanceResults.rhythmicAnalysis.dominantPattern && (
                        <div>
                          <h5 className="text-sm font-medium mb-1 text-forest-200">
                            Dominant Pattern:
                          </h5>
                          <p className="text-sm text-leaf-300">
                            {
                              performanceResults.rhythmicAnalysis
                                .dominantPattern
                            }
                          </p>
                        </div>
                      )}
                      {performanceResults.rhythmicAnalysis.grooveFeel && (
                        <div>
                          <h5 className="text-sm font-medium mb-1 text-forest-200">
                            Groove Feel:
                          </h5>
                          <span className="inline-block px-2 py-1 rounded text-sm bg-leaf-900 text-leaf-200">
                            {performanceResults.rhythmicAnalysis.grooveFeel}
                          </span>
                        </div>
                      )}
                      {performanceResults.rhythmicAnalysis.tempoImplication && (
                        <div>
                          <h5 className="text-sm font-medium mb-1 text-forest-200">
                            Tempo Implication:
                          </h5>
                          <p className="text-sm text-leaf-300">
                            {
                              performanceResults.rhythmicAnalysis
                                .tempoImplication
                            }
                          </p>
                        </div>
                      )}
                      {performanceResults.rhythmicAnalysis.syncopation && (
                        <div>
                          <h5 className="text-sm font-medium mb-1 text-forest-200">
                            Syncopation:
                          </h5>
                          <p className="text-sm text-leaf-300">
                            {performanceResults.rhythmicAnalysis.syncopation}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Singability Score Section (NEW) */}
                {performanceResults.singabilityScore && (
                  <div className="p-4 rounded border bg-forest-700 border-forest-600">
                    <h4 className="font-medium mb-3 text-ember-400">
                      🎤 Singability Score
                    </h4>
                    <div className="grid gap-3 md:grid-cols-4 mb-3">
                      {performanceResults.singabilityScore.overall && (
                        <div className="p-3 rounded text-center bg-forest-800">
                          <div
                            className={`text-2xl font-bold ${
                              performanceResults.singabilityScore.overall >= 8
                                ? "text-leaf-500"
                                : performanceResults.singabilityScore.overall >=
                                    5
                                  ? "text-gold-500"
                                  : "text-ember-500"
                            }`}
                          >
                            {performanceResults.singabilityScore.overall}/10
                          </div>
                          <div className="text-xs text-forest-300">Overall</div>
                        </div>
                      )}
                      {performanceResults.singabilityScore.memorability && (
                        <div className="p-3 rounded text-center bg-forest-800">
                          <div className="text-2xl font-bold text-ember-400">
                            {performanceResults.singabilityScore.memorability}
                            /10
                          </div>
                          <div className="text-xs text-forest-300">
                            Memorability
                          </div>
                        </div>
                      )}
                      {performanceResults.singabilityScore
                        .technicalDifficulty && (
                        <div className="p-3 rounded text-center bg-forest-800">
                          <div className="text-2xl font-bold text-ember-400">
                            {
                              performanceResults.singabilityScore
                                .technicalDifficulty
                            }
                            /10
                          </div>
                          <div className="text-xs text-forest-300">
                            Tech. Difficulty
                          </div>
                        </div>
                      )}
                      {performanceResults.singabilityScore.rangeRequirement && (
                        <div className="p-3 rounded text-center bg-forest-800">
                          <div className="text-lg font-bold text-ember-400">
                            {
                              performanceResults.singabilityScore
                                .rangeRequirement
                            }
                          </div>
                          <div className="text-xs text-forest-300">
                            Range Required
                          </div>
                        </div>
                      )}
                    </div>
                    {performanceResults.singabilityScore.assessmentNotes && (
                      <p className="text-sm text-forest-200">
                        {performanceResults.singabilityScore.assessmentNotes}
                      </p>
                    )}
                  </div>
                )}

                {/* Production Notes Section (NEW) */}
                {performanceResults.productionNotes && (
                  <div className="p-4 rounded border bg-forest-700 border-forest-600">
                    <h4 className="font-medium mb-3 text-forest-200">
                      🎛️ Production Recommendations
                    </h4>
                    <div className="grid gap-3 md:grid-cols-2">
                      {performanceResults.productionNotes
                        .suggestedInstrumentation?.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium mb-2 text-forest-200">
                            Suggested Instruments:
                          </h5>
                          <div className="flex flex-wrap gap-1">
                            {performanceResults.productionNotes.suggestedInstrumentation.map(
                              (inst, index) => (
                                <span
                                  key={index}
                                  className="text-xs px-2 py-1 rounded bg-forest-600 text-forest-200"
                                >
                                  {inst}
                                </span>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                      {performanceResults.productionNotes.sonicTexture && (
                        <div>
                          <h5 className="text-sm font-medium mb-1 text-forest-200">
                            Sonic Texture:
                          </h5>
                          <p className="text-sm text-forest-200">
                            {performanceResults.productionNotes.sonicTexture}
                          </p>
                        </div>
                      )}
                    </div>
                    {performanceResults.productionNotes.arrangementIdeas && (
                      <div className="mt-3">
                        <h5 className="text-sm font-medium mb-1 text-forest-200">
                          Arrangement Ideas:
                        </h5>
                        <p className="text-sm text-forest-200">
                          {performanceResults.productionNotes.arrangementIdeas}
                        </p>
                      </div>
                    )}
                    {performanceResults.productionNotes.mixApproach && (
                      <div className="mt-3">
                        <h5 className="text-sm font-medium mb-1 text-forest-200">
                          Mix Approach:
                        </h5>
                        <p className="text-sm text-forest-200">
                          {performanceResults.productionNotes.mixApproach}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Overall Performance Notes (NEW) */}
                {performanceResults.overallPerformanceNotes && (
                  <div className="p-4 rounded border bg-forest-800 border-forest-600">
                    <h4 className="font-medium mb-3 text-leaf-400">
                      📝 Performance Guide
                    </h4>
                    <p className="text-sm whitespace-pre-line text-forest-200">
                      {performanceResults.overallPerformanceNotes}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div
                className={`p-4 rounded border ${
                  performanceResults.needsApiKey
                    ? "bg-gold-900/30 border-gold-700"
                    : "bg-ember-900/30 border-ember-700"
                }`}
              >
                {performanceResults.needsApiKey ? (
                  <div>
                    <p className="text-sm font-medium mb-2 text-gold-300">
                      🔑 API Key Required
                    </p>
                    <p className="text-sm mb-2 text-gold-400">
                      AI analysis requires a Google Gemini API key. Go to{" "}
                      <strong>More → AI Settings</strong> to add your free API
                      key.
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm mb-3 text-ember-300">
                      ❌ Analysis failed: {performanceResults.error}
                    </p>
                    {performanceResults.retryable && (
                      <button
                        onClick={() => handlePerformanceAnalysis(true)}
                        className="px-3 py-1 rounded text-sm transition-colors bg-ember-800 hover:bg-ember-700 text-ember-200"
                      >
                        Try Again (Force Fresh)
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalysisTab;
